/**
 * OAuth2 Authorization Code + PKCE — Aucun secret côté client.
 *
 * Flux :
 * 1. Génère code_verifier (random 64 bytes, base64url)
 * 2. Dérive code_challenge = BASE64URL(SHA256(code_verifier))
 * 3. POST /api/oauth/authorize → reçoit un auth `code` (60s TTL)
 * 4. POST /api/oauth/token    → échange code + code_verifier → access_token JWT
 * 5. Ajoute X-App-Token à chaque requête axios
 * 6. Renouvelle automatiquement avant expiration
 *
 * Fallback: Client Credentials si REACT_APP_OAUTH_CLIENT_SECRET est défini (serveur/scripts).
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_URL = `${BACKEND_URL}/api/oauth/token`;
const AUTHORIZE_URL = `${BACKEND_URL}/api/oauth/authorize`;

const CLIENT_ID = process.env.REACT_APP_OAUTH_CLIENT_ID || 'nannyou-app';
const CLIENT_SECRET = process.env.REACT_APP_OAUTH_CLIENT_SECRET || '';

let _accessToken = null;
let _expiresAt = 0;
let _refreshTimer = null;
let _tokenPromise = null;

// CSRF protection: set X-Requested-With on ALL axios requests (Phase 15)
// This header proves the request comes from our SPA, not a forged cross-origin form
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// ── PKCE helpers (Web Crypto API) ──

function _generateCodeVerifier() {
  const array = new Uint8Array(64);
  crypto.getRandomValues(array);
  return _base64UrlEncode(array);
}

function _base64UrlEncode(buffer) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function _deriveCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return _base64UrlEncode(digest);
}

// ── Token fetch (PKCE or Client Credentials) ──

async function _fetchTokenPKCE() {
  // Step 1: Generate PKCE pair
  const codeVerifier = _generateCodeVerifier();
  const codeChallenge = await _deriveCodeChallenge(codeVerifier);

  // Step 2: Get auth code
  const authRes = await axios.post(AUTHORIZE_URL, {
    client_id: CLIENT_ID,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  }, {
    headers: { 'Content-Type': 'application/json' },
    // No X-App-Token for this request
    transformRequest: [(data, headers) => {
      delete headers['X-App-Token'];
      return JSON.stringify(data);
    }],
  });

  const { code } = authRes.data;

  // Step 3: Exchange code + verifier for token
  const tokenRes = await axios.post(TOKEN_URL, {
    grant_type: 'authorization_code',
    code,
    code_verifier: codeVerifier,
    client_id: CLIENT_ID,
  }, {
    headers: { 'Content-Type': 'application/json' },
    transformRequest: [(data, headers) => {
      delete headers['X-App-Token'];
      return JSON.stringify(data);
    }],
  });

  return tokenRes.data;
}

async function _fetchTokenClientCredentials() {
  const res = await axios.post(TOKEN_URL, {
    grant_type: 'client_credentials',
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
  }, {
    headers: { 'Content-Type': 'application/json' },
    transformRequest: [(data, headers) => {
      delete headers['X-App-Token'];
      return JSON.stringify(data);
    }],
  });
  return res.data;
}

async function fetchToken() {
  try {
    // Use PKCE if no client_secret (SPA), fallback to CC if secret present (server/scripts)
    const data = CLIENT_SECRET
      ? await _fetchTokenClientCredentials()
      : await _fetchTokenPKCE();

    const { access_token, expires_in } = data;
    _accessToken = access_token;
    _expiresAt = Date.now() + expires_in * 1000;

    // Set global default header
    axios.defaults.headers.common['X-App-Token'] = access_token;

    // Schedule refresh 5 min before expiration (or half-life if < 10 min)
    const refreshIn = Math.max((expires_in - 300) * 1000, (expires_in / 2) * 1000);
    if (_refreshTimer) clearTimeout(_refreshTimer);
    _refreshTimer = setTimeout(() => {
      _tokenPromise = null;
      fetchToken();
    }, refreshIn);

    return access_token;
  } catch (err) {
    console.error('[OAuth2/PKCE] Échec obtention token:', err?.response?.data || err.message);
    if (_refreshTimer) clearTimeout(_refreshTimer);
    _refreshTimer = setTimeout(() => {
      _tokenPromise = null;
      fetchToken();
    }, 10000);
    throw err;
  }
}

/**
 * Get the current valid token, or fetch a new one.
 */
export async function getAppToken() {
  if (_accessToken && Date.now() < _expiresAt - 30000) {
    return _accessToken;
  }
  if (!_tokenPromise) {
    _tokenPromise = fetchToken();
  }
  return _tokenPromise;
}

/**
 * Initialize OAuth2 PKCE: fetch first token and set up axios interceptor.
 */
export async function initOAuth2() {
  await getAppToken();

  // Response interceptor: auto-refresh on expired app token
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (
        error.response?.status === 401 &&
        error.response?.data?.error === 'invalid_token' &&
        !originalRequest._oauthRetry
      ) {
        originalRequest._oauthRetry = true;
        _accessToken = null;
        _tokenPromise = null;
        try {
          const newToken = await getAppToken();
          originalRequest.headers['X-App-Token'] = newToken;
          return axios.request(originalRequest);
        } catch {
          return Promise.reject(error);
        }
      }
      return Promise.reject(error);
    }
  );
}

/**
 * Clean up.
 */
export function destroyOAuth2() {
  if (_refreshTimer) clearTimeout(_refreshTimer);
  _accessToken = null;
  _expiresAt = 0;
  _tokenPromise = null;
  delete axios.defaults.headers.common['X-App-Token'];
}
